/**
 * Editing what a derived value is made of, from wherever the value is shown.
 *
 * Rule R1 (nd-011, nd-030): people resolve inputs, never outputs. A cap rate
 * the engine computes as NOI / value is never an input box — a typed 9.0 over
 * Westbrook's 7.05 stopped recomputing when NOI changed, and the server now
 * refuses that write with a 422. Until now the unlocked dossier said so in a
 * tooltip and stopped there: the reader had to remember the formula, find NOI
 * and the value somewhere else on the page (or under Audit > All fields) and
 * correct them there.
 *
 * So an unlocked derived value is a control that opens its inputs, in place,
 * each one the same input the rest of the dossier uses and each one writing
 * through the same draft and the same Save. Nothing here saves anything: it
 * only calls `setField`, exactly as the Audit tab's catalogue row does.
 *
 * This module does not import `ui.tsx` (whose EditableSlot uses it); the input
 * is handed in as `renderInput`, so the two never import each other.
 */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

import { useDossierEdit, type DossierEdit } from './editContext'
import { isSystemField, isWritableField, unwritableReason } from './editableFields'
import { formatFieldValue } from './format'

/** Draws the catalogue input for one field (SlotValueInput, from ui.tsx). */
export type RenderInput = (name: string, label: string) => ReactNode

/**
 * The formula of a value the dossier computes rather than holds: one the
 * engine derived and stored (lineage Derived), or one the server's preview has
 * just recalculated from the person's unsaved edits (type NOI, and the DSCR
 * follows — on Washington Square the bar's DSCR turned into an input holding
 * the preview's 1.31x). Both are outputs; the second becomes the first on
 * Save. Null for an input.
 */
export function outputFormulaOf(edit: DossierEdit, name: string): string | null {
  const stored = edit.derivedFormula?.(name) ?? null
  if (stored !== null) return stored
  // Optional-called: a few test doubles of the context omit it.
  return edit.isDerivedField?.(name) ? edit.derivation?.notes?.[name] ?? 'recalculated from your unsaved edits' : null
}

/** The field's catalogue label, else its key read as words. */
export function fieldLabel(edit: Pick<DossierEdit, 'defs'>, name: string): string {
  return edit.defs.find((d) => d.name === name)?.label ?? name.replace(/_/g, ' ')
}

/**
 * The inputs to offer for a derived value: the caller's own list when it
 * computes the figure itself (Avg unit size is SF / units on the page), else
 * what the dossier knows of the field (recorded lineage inputs, then the
 * catalogue rules). The output itself and identity fields are never offered.
 */
export function inputsFor(edit: DossierEdit, name: string, explicit?: string[]): string[] {
  const names = explicit ?? edit.inputsOf?.(name) ?? []
  return [...new Set(names)].filter((n) => Boolean(n) && n !== name && !isSystemField(n))
}

/** Space and Enter open a span that acts as a button, as they would a button. */
export function activateOnKey(open: (target: HTMLElement) => void) {
  return (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    open(event.currentTarget)
  }
}

/**
 * A small panel anchored under (or, near the foot of the window, over) the
 * value that opened it.
 *
 * Portaled to the body because nearly every dossier panel clips its content
 * (`overflow-hidden`, so the one-screen layout never grows); drawn inside it,
 * the editor was cut off by the panel it was editing. It carries the
 * `dossier-shell` class so the edit-mode input styles apply out there too.
 * React events still bubble through the dossier (portals keep the React
 * tree), so a click in here never reaches the backdrop that closes the record.
 *
 * Escape closes this panel and nothing else: the dossier shell listens for
 * Escape on window to close the whole record, so the key is taken in the
 * capture phase and stopped there, as the full-size panel modal does.
 */
export function EditPopover({
  anchor,
  title,
  note,
  footer,
  children,
  onClose,
}: {
  anchor: HTMLElement
  title: string
  note?: ReactNode
  /** The panel's last line, when it is not the field editor's "Done" (the
   *  header Save's reason prompt ends with its own Save). */
  footer?: ReactNode
  children: ReactNode
  onClose: () => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [place, setPlace] = useState<{ top?: number; bottom?: number; left: number }>({ top: -9999, left: -9999 })
  const WIDTH = 320

  useLayoutEffect(() => {
    const measure = () => {
      const rect = anchor.getBoundingClientRect()
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - WIDTH - 8))
      const height = ref.current?.offsetHeight ?? 180
      const fitsBelow = rect.bottom + 4 + height <= window.innerHeight - 8
      setPlace(
        fitsBelow
          ? { top: rect.bottom + 4, left }
          : { bottom: Math.max(8, window.innerHeight - rect.top + 4), left },
      )
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [anchor])

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('input, select, textarea')?.focus()
    return () => {
      if (anchor.isConnected) anchor.focus()
    }
  }, [anchor])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onClose()
    }
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node | null
      if (!target || ref.current?.contains(target) || anchor.contains(target)) return
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    document.addEventListener('mousedown', onDown, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      document.removeEventListener('mousedown', onDown, true)
    }
  }, [anchor, onClose])

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label={title}
      data-testid="edit-popover"
      className="dossier-shell fixed z-[230] rounded-md border border-amber-400/25 bg-[#0b0b0b] p-3 text-left font-sans shadow-2xl"
      style={{ ...place, width: WIDTH }}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className="min-w-0 font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-amber-300/90">
          {title}
        </span>
        <button type="button" onClick={onClose} aria-label="Close" className="shrink-0 text-stone-500 hover:text-stone-200">
          <X size={12} />
        </button>
      </div>
      {note ? <div className="mb-2 text-[10px] leading-snug text-stone-400">{note}</div> : null}
      {children}
      <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-white/[0.06] pt-2">
        {footer ?? (
          <>
            <span className="text-[9px] leading-snug text-stone-600">Saved with the dossier&rsquo;s Save, against your name.</span>
            <button type="button" onClick={onClose} className="shrink-0 text-[10px] text-amber-300 hover:text-amber-200">
              Done
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}

/**
 * One row per field: its label, and the catalogue input when a save can write
 * it. An input that is itself derived, or that no column holds, is shown as a
 * value with the reason — never offered as a box the server would refuse.
 */
export function InputEditorList({
  names,
  renderInput,
  labels,
}: {
  names: string[]
  renderInput: RenderInput
  /** Labels a caller states for these fields ("Year Renovated" beside a
   *  composite's "Year Built"); the catalogue's otherwise. */
  labels?: Record<string, string>
}) {
  const edit = useDossierEdit()
  if (names.length === 0) return null
  return (
    <ul className="space-y-1.5" data-testid="edit-inputs">
      {names.map((name) => {
        const def = edit.defs.find((d) => d.name === name)
        const label = labels?.[name] ?? fieldLabel(edit, name)
        const formula = outputFormulaOf(edit, name)
        const writable = isWritableField(name, edit.defs) && !isSystemField(name) && formula === null
        const shown = formatFieldValue(edit.effectiveValue(name), def?.kind, name) || '—'
        return (
          <li
            key={name}
            data-edit-input={name}
            className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] items-center gap-2 text-[11px]"
          >
            <span className="min-w-0 truncate text-stone-400" title={label}>
              {label}
            </span>
            {writable ? (
              <span className="block min-w-0 font-mono text-stone-100">{renderInput(name, label)}</span>
            ) : (
              <span
                className={`min-w-0 truncate font-mono ${formula ? 'text-violet-300' : 'text-stone-300'}`}
                title={
                  formula
                    ? `Derived: ${formula}. Itself computed — correct the figures it is made of.`
                    : unwritableReason(name)
                }
              >
                {shown}
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/**
 * A derived value, unlocked: the figure stays exactly where and how it is
 * drawn, with a dotted underline, and opens its inputs.
 *
 * A span acting as a button rather than a <button>: a button is laid out as an
 * inline block, which would lift the line by its own box; a span takes no room
 * the figure did not already take, so unlocking moves nothing (owner request,
 * 25 Sep). A figure with no named inputs (one the page reads off the rent
 * roll) says what it is computed from and offers nothing to click.
 */
export function ComputedTrigger({
  name,
  label,
  formula,
  inputs,
  renderInput,
  children,
}: {
  name: string
  label: string
  formula?: string | null
  inputs?: string[]
  renderInput: RenderInput
  children: ReactNode
}) {
  const edit = useDossierEdit()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const names = inputsFor(edit, name, inputs)
  const said = formula && formula.trim() ? formula.trim() : 'computed from other fields'
  const hint = names.length
    ? `Computed: ${said}. Edit its inputs — ${names.map((n) => fieldLabel(edit, n)).join(', ')} — not the result.`
    : `Computed: ${said}. Correct the figures it is made of, not the result.`
  if (names.length === 0) {
    return (
      <span data-edit-computed={name} title={hint}>
        {children}
      </span>
    )
  }
  const open = (target: HTMLElement) => setAnchor((current) => (current ? null : target))
  return (
    <>
      <span
        role="button"
        tabIndex={0}
        data-edit-computed={name}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        title={hint}
        onClick={(event) => open(event.currentTarget)}
        onKeyDown={activateOnKey(open)}
        className="cursor-pointer underline decoration-violet-300/60 decoration-dotted underline-offset-2 hover:decoration-violet-200 focus-visible:outline-none focus-visible:decoration-solid"
      >
        {children}
      </span>
      {anchor ? (
        <EditPopover
          anchor={anchor}
          title={`${label} · its inputs`}
          note={<>Computed: <span className="font-mono text-violet-300">{said}</span>. It recomputes from these.</>}
          onClose={() => setAnchor(null)}
        >
          <InputEditorList names={names} renderInput={renderInput} />
        </EditPopover>
      ) : null}
    </>
  )
}
